import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, Checkbox, Input, Spinner } from "../../shared";
import { SettingsHeader } from ".";
import { PERMISSIONS } from "../../constants/access-control";
import { usePermissions } from "../../hooks/usePermissions";
import {
  useAssignUserRolesMutation,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionsQuery,
  useGetRolesQuery,
  useUpdateRoleMutation,
} from "../../redux/services/roles/rolesApi";
import { useGetUsersQuery } from "../../redux/services/users/usersApi";

const emptyRoleForm = {
  name: "",
  description: "",
  permissions: [],
};

const getUserId = (user) => user?.id || user?._id;

const getUsersFromResponse = (data) =>
  data?.users || data?.data?.users || data?.data || [];

const RolesPermissions = () => {
  const { hasPermission } = usePermissions();
  const canReadRoles = hasPermission(PERMISSIONS.SETTINGS_ROLES_READ);
  const canManageRoles = hasPermission(PERMISSIONS.SETTINGS_ROLES_MANAGE);
  const {
    data: roles = [],
    error: rolesError,
    isLoading: rolesLoading,
  } = useGetRolesQuery(undefined, { skip: !canReadRoles });
  const {
    data: permissionGroups = [],
    error: permissionsError,
    isLoading: permissionsLoading,
  } = useGetPermissionsQuery(undefined, { skip: !canReadRoles });
  const {
    data: usersData,
    error: usersError,
    isLoading: usersLoading,
  } = useGetUsersQuery(
    {
      page: 1,
      limit: 250,
    },
    { skip: !canReadRoles }
  );
  const [createRole, { isLoading: creating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: updating }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: deleting }] = useDeleteRoleMutation();
  const [assignUserRoles, { isLoading: assigning }] =
    useAssignUserRolesMutation();

  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [form, setForm] = useState(emptyRoleForm);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserRoleIds, setSelectedUserRoleIds] = useState([]);

  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedRoleId),
    [roles, selectedRoleId]
  );
  const users = useMemo(() => getUsersFromResponse(usersData), [usersData]);
  const selectedUser = useMemo(
    () => users.find((user) => getUserId(user) === selectedUserId),
    [users, selectedUserId]
  );
  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    if (!query) return users;

    return users.filter((user) => {
      const label = `${user.fullName || ""} ${user.email || ""}`.toLowerCase();
      return label.includes(query);
    });
  }, [userSearch, users]);

  useEffect(() => {
    if (!isCreatingRole && !selectedRoleId && roles.length) {
      setSelectedRoleId(roles[0].id);
    }
  }, [isCreatingRole, roles, selectedRoleId]);

  useEffect(() => {
    if (isCreatingRole) {
      setForm(emptyRoleForm);
    } else if (selectedRole) {
      setForm({
        name: selectedRole.name || "",
        description: selectedRole.description || "",
        permissions: selectedRole.permissions || [],
      });
    } else {
      setForm(emptyRoleForm);
    }
  }, [isCreatingRole, selectedRole]);

  useEffect(() => {
    if (!selectedUserId && users.length) {
      setSelectedUserId(getUserId(users[0]));
    }
  }, [users, selectedUserId]);

  useEffect(() => {
    const roleIds = selectedUser?.tenantRoles || [];
    setSelectedUserRoleIds(roleIds.map((role) => role.id || role._id || role));
  }, [selectedUser]);

  const isSaving = creating || updating || deleting || assigning;
  const permissionDenied = [rolesError, permissionsError, usersError].some(
    (error) => error?.data?.code === "PERMISSION_DENIED"
  );

  /**
   * Compact in-panel loader. Used in place of the previous page-wide
   * `<PageLoading />` so each section can render independently of the others
   * (e.g. the Roles list can be ready while the Users list is still loading).
   */
  const SectionLoader = ({ label }) => (
    <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
      <Spinner size="sm" />
      <span>{label}</span>
    </div>
  );

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const togglePermission = (permissionKey) => {
    setForm((current) => {
      const exists = current.permissions.includes(permissionKey);
      const permissions = exists
        ? current.permissions.filter((permission) => permission !== permissionKey)
        : [...current.permissions, permissionKey];

      return { ...current, permissions };
    });
  };

  const handleNewRole = () => {
    setIsCreatingRole(true);
    setSelectedRoleId(null);
    setForm(emptyRoleForm);
  };

  const handleSaveRole = async () => {
    try {
      if (!form.name.trim()) {
        toast.warning("Role name is required.");
        return;
      }

      if (selectedRole?.id) {
        await updateRole({
          id: selectedRole.id,
          data: form,
        }).unwrap();
        toast.success("Role updated.");
        return;
      }

      const result = await createRole(form).unwrap();
      setIsCreatingRole(false);
      setSelectedRoleId(result?.role?.id || null);
      toast.success("Role created.");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to save role.");
    }
  };

  const handleDisableRole = async () => {
    if (!selectedRole?.id) return;

    try {
      await deleteRole(selectedRole.id).unwrap();
      setIsCreatingRole(false);
      setSelectedRoleId(null);
      toast.success("Role disabled.");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to disable role.");
    }
  };

  const toggleUserRole = (roleId) => {
    setSelectedUserRoleIds((current) =>
      current.includes(roleId)
        ? current.filter((id) => id !== roleId)
        : [...current, roleId]
    );
  };

  const handleSaveUserRoles = async () => {
    if (!selectedUserId) return;

    try {
      await assignUserRoles({
        id: selectedUserId,
        tenantRoles: selectedUserRoleIds,
      }).unwrap();
      toast.success("User roles updated.");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to update user roles.");
    }
  };

  if (!canReadRoles || permissionDenied) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        You do not have permission to manage roles and permissions.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SettingsHeader
        title="Roles & Permissions"
        actions={
          canManageRoles && (
            <Button type="alternative" size="sm" onClick={handleNewRole}>
              New Role
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
          <div>
            <div className="font-semibold text-gray-900">Roles</div>
            <p className="text-sm text-gray-500">
              System roles preserve existing access. Custom roles can be
              tailored per customer.
            </p>
          </div>
          {rolesLoading ? (
            <SectionLoader label="Loading roles..." />
          ) : (
            <div className="space-y-2">
              {roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => {
                    setIsCreatingRole(false);
                    setSelectedRoleId(role.id);
                  }}
                  className={`w-full rounded-lg border p-3 text-left text-sm ${
                    !isCreatingRole && selectedRoleId === role.id
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{role.name}</span>
                    {role.isSystemRole && (
                      <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                        System
                      </span>
                    )}
                  </div>
                  {role.description && (
                    <div className="mt-1 text-gray-500">{role.description}</div>
                  )}
                </button>
              ))}
              {!roles.length && (
                <div className="p-2 text-sm text-gray-500">No roles yet.</div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 xl:col-span-2">
          <div>
            <div className="font-semibold text-gray-900">
              {selectedRole?.id ? "Role Details" : "Create Role"}
            </div>
            <p className="text-sm text-gray-500">
              Choose exactly what this role can access in the app and API.
            </p>
          </div>

          <Input
            label="Role name"
            value={form.name}
            disabled={selectedRole?.isSystemRole || !canManageRoles}
            onChange={(target) => updateField("name", target.value)}
          />
          <Input
            label="Description"
            value={form.description}
            disabled={!canManageRoles}
            onChange={(target) => updateField("description", target.value)}
          />

          {permissionsLoading ? (
            <SectionLoader label="Loading permissions..." />
          ) : (
            <div className="space-y-4">
              {permissionGroups.map((group) => (
                <div
                  key={group.group}
                  className="rounded-lg border border-gray-200 p-4"
                >
                  <div className="font-medium text-gray-900">{group.group}</div>
                  <div className="text-sm text-gray-500">{group.description}</div>
                  <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                    {group.permissions.map((permission) => (
                      <Checkbox
                        key={permission.key}
                        id={`permission-${permission.key}`}
                        label={permission.label}
                        checked={form.permissions.includes(permission.key)}
                        onChange={() => togglePermission(permission.key)}
                        disabled={!canManageRoles}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {canManageRoles && (
            <div className="flex gap-2">
              <Button loading={isSaving} onClick={handleSaveRole}>
                Save Role
              </Button>
              {selectedRole?.id && !selectedRole?.isSystemRole && (
                <Button
                  type="danger"
                  loading={isSaving}
                  onClick={handleDisableRole}
                >
                  Disable Role
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
          <div>
            <div className="font-semibold text-gray-900">Assign Users</div>
            <p className="text-sm text-gray-500">
              Assign tenant roles without changing platform authority.
            </p>
          </div>

          <Input
            label="Search users"
            placeholder="Search by name or email"
            value={userSearch}
            disabled={!canManageRoles}
            onChange={(target) => setUserSearch(target.value)}
          />

          <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-2">
            {usersLoading ? (
              <SectionLoader label="Loading users..." />
            ) : (
              <>
                {filteredUsers.map((user) => {
                  const userId = getUserId(user);
                  return (
                    <button
                      key={userId}
                      type="button"
                      onClick={() => setSelectedUserId(userId)}
                      disabled={!canManageRoles}
                      className={`w-full rounded-lg p-2 text-left text-sm ${
                        selectedUserId === userId
                          ? "bg-primary-50 text-primary-700"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <span className="block font-medium">
                        {user.fullName || "Unnamed User"}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {user.email}
                      </span>
                    </button>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <div className="p-2 text-sm text-gray-500">
                    No users found.
                  </div>
                )}
              </>
            )}
          </div>

          {rolesLoading ? (
            <SectionLoader label="Loading roles..." />
          ) : (
            <div className="space-y-2">
              {roles.map((role) => (
                <Checkbox
                  key={`user-role-${role.id}`}
                  id={`user-role-${role.id}`}
                  label={role.name}
                  checked={selectedUserRoleIds.includes(role.id)}
                  onChange={() => toggleUserRole(role.id)}
                  disabled={!canManageRoles}
                />
              ))}
            </div>
          )}

          {canManageRoles && (
            <Button loading={isSaving} onClick={handleSaveUserRoles}>
              Save User Roles
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;
